<template>
  <div
    class="flex flex-col slide-generator"
    :class="{
      'expand': isGeneratorContentVisible,
      'is-modal-content': isModalContent,
      '--has-expanded-examples': isExpandExampleList,
      '--has-search-content': isSearchAIModalContentVisible,
    }"
  >
    <div
      class="quiz-header"
      :class="{
        'expand': isGeneratorContentVisible
      }"
    >
      <div class="quiz-header-inner">
        <AISlidesGeneratorHeader
          :isModalContent="isModalContent"
          :isSearchAIModalContentVisible="isSearchAIModalContentVisible"
          :isGeneratorContentVisible="isGeneratorContentVisible"
          :hasInputPrompt="hasInputPrompt"
          @backToChooseSlideType="backToChooseSlideType"
        >
          <template #title>
            <slot name="title">
            </slot>
          </template>
        </AISlidesGeneratorHeader>

        <aha-textarea
          class="slide-generator-input"
          :class="{'hidden': isAITextAreaHidden}"
          id="slide-generator-input"
          :placeholder="quizPlaceholder"
          :max-length="PROMPT_MAX_LENGTH"
          v-model="prompt"
          @autoExpand="autoExpandTextArea"
          @focus="focusAIInput"
          @keyupEnter="handleGenerateSlides('Enter')"
          hasButtonOnEnd
          hasButtonAtStart
          preventEnterNewLine
          @buttonClick="generateOrStopGeneratingSlides('Button clicked')"
          @buttonAtStartClick="openModalDocToQuiz()"
        >
          <template #buttonAtStart>
            <aha-icon
              v-if="isModalContent && !isSearchAIModalContentVisible"
              name="clip"
              class="doc-to-quiz__upload-icon"
              :class="{'disabled': isDocToSlideGenerating}"
              v-tooltip="docToQuizAttachmentTooltip"
            ></aha-icon>
            <aha-icon
              v-if="!isModalContent"
              name="clip"
              class="doc-to-quiz__upload-icon"
              :class="{'disabled': isDocToSlideGenerating}"
              v-tooltip="docToQuizAttachmentTooltip"
            ></aha-icon>
            <div class="aha-textarea__button-bg" :class="{'hidden': isDocToSlideGenerating}"></div>
          </template>
          <template #button>
            <slot name="button">
              <AIIsGeneratingButton
                v-if="!isDoneGenerating"
                :resetProgressBar="resetProgressBar"
                :isSubmitting="isSubmitting"
                :isSubmittingFeedback="isSubmittingFeedback"
              />
              <aha-icon
                v-if="isDoneGenerating"
                name="send-rotate"
                v-tooltip="(isSubmitting || !isDoneGenerating) ? '' : buttonTooltipContent"
                class="generate_send-icon"
                :class="generateButtonIconClass"
              />
              <div class="aha-textarea__button-bg" :class="{'hidden': isGenerateButtonDisabled}"></div>
            </slot>
          </template>
        </aha-textarea>
        <AISlideGeneratorExamples
          :class="{'hidden': isExampleAreaHidden}"
          :isModalContent="isModalContent"
          :preventTracking="preventTrackingDefault"
          :isExpandExampleList="isExpandExampleList || isExamplesExpandedByDefault"
          :customSamples="customSamples"
          @expandExampleList="expandExampleList()"
          @handleGenerateSlides="handleGenerateSlides"
        />
      </div>
    </div>
    <AISlidesGeneratorResultLayout
      v-if="isGeneratedResultsVisible"
      class="search-result"
      :prompt="prompt"
      :isSubmitting="isSubmitting"
      :isSubmittingFeedback="isSubmittingFeedback"
      :resetProgressBar="resetProgressBar"
      :chatResponseError="chatResponseError"
      :chatResponse="chatResponse"
      :hasStoppedGenerating="hasStoppedGenerating"
      :isContentListLoading="!isDoneGenerating"
      :isReturnedSlideError="isReturnedSlideError"
      @handleAddSlideToPresentation="handleAddSlideToPresentation"
      @handleGenerateSlides="handleGenerateSlides"
      :customAISlideStumpedText="customAISlideStumpedText"
    />
    <AIFeedbackButtons
      v-if="isGeneratedResultsVisible"
      @handleFeedbackQuiz="handleFeedbackQuiz"
      :isFeedbackButtonsVisible="isFeedbackButtonsVisible"
      :allFeedbackButtonTexts="allFeedbackButtonTexts"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import { get as lodashGet } from 'lodash'
import mixpanelUtils from '@/utils/mixpanel.utils'
import textUtils from '@/utils/textUtil'
import eventCategories from '@/constant/eventCategories'
import AIResponseType from '@/constant/aiResponseType'

import { generateBlockHash, generateBlockId } from '@/utils/canvas-block-layouts/common'
import utils from '@/util'
import OpenAIApi from '@/api/openai'
import SlideApi from '@/api/slide'
import AIResponseAPI from '@/api/aiResponse'
import EventBus from '@/eventBus'
import * as localStorageExpiry from '@/utils/local.storage.expiry'
import PresentationUtils from '@/utils/presentation'
import OpenAIUtils from '@/utils/open-ai/OpenAIUtils'

// components
import AISlidesGeneratorHeader from '@/components-v2/presentation-editor/new-slide/OpenAI/AISlidesGeneratorHeader.vue'
import AISlideGeneratorExamples from '@/components-v2/presentation-editor/new-slide/OpenAI/AISlideGeneratorExamples.vue'
import AIFeedbackButtons from '@/components-v2/openAI/AIFeedbackButtons.vue'
import AISlidesGeneratorResultLayout from '@/components-v2/presentation-editor/new-slide/OpenAI/AISlidesGeneratorResultLayout.vue'
import AIIsGeneratingButton from '@/components-v2/openAI/AIIsGeneratingButton.vue'

import CanvasBlocksUpdateMixin from '@/mixins/presentation/CanvasBlocksUpdateMixin'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import openAIConstants from '@/constant/openAI'
import { AI_SLIDES_GENERATOR_REFINE_RESULTS } from '@/constant/eventActions'

// trackers
import SlideMixpanelTracker from '@/utils/mixpanel/slide.mixpanel-tracker'
import PresentationMixpanelTracker from '@/utils/mixpanel/presentation.mixpanel-tracker'

const REQUEST_MAXIMUM = 150
const REQUEST_THRESHOLD = 140
const DAYS_30 = 30 * 24 * 60 * 60 * 1000
const PROMPT_MAX_LENGTH = 500
const INPUT_MODE = {
  ENTER: 'Enter',
  BUTTON_CLICKED: 'Button clicked',
  FROM_URL: 'From URL',
}

export default {
  components: {
    AISlidesGeneratorHeader,
    AISlideGeneratorExamples,
    AIFeedbackButtons,
    AISlidesGeneratorResultLayout,
    AIIsGeneratingButton,
  },
  mixins: [CanvasBlocksUpdateMixin, UndoRedoMixin],
  props: {
    isGeneratorContentVisible: {
      type: Boolean,
      default: false,
    },
    isModalContent: {
      type: Boolean,
      default: false,
    },
    placeholderCustom: {
      type: String,
      default: '',
    },
    isExamplesExpandedByDefault: {
      type: Boolean,
      default: false,
    },
    preventTrackingDefault: {
      type: Boolean,
      default: false,
    },
    customSamples: {
      type: Array,
      default: () => [],
    },
    handleOpenModalDocToQuiz: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      prompt: '',
      mode: '',
      chatResponse: '',
      chatResponseError: false,
      isSubmitting: false,
      exampleList: [0, 1, 2, 3, 4], // fake for skeleton loader
      isExpandExampleList: false,
      gaGenerateFailedAction: 'AI Quiz Generator > Generate > Failed',
      gaGenerateSuccessfulAction: 'AI Quiz Generator > Generate > Successful',
      AIResponseType,
      isSearchAIModalContentVisible: false,
      buttonTooltipContent: null,
      tooltipText: 'Generate',
      hasReachedTooltipAutoShownLimit: false,
      resetProgressBar: 0,
      isReturnedSlideError: false,
      isExampleResponse: false,
      isDoneGenerating: true,
      isAddSlideButtonsDisabled: false,
      isAllResponsesCreated: false,
      PROMPT_MAX_LENGTH,
      allFeedbackButtonTexts: openAIConstants.aiSlidesGeneratorFeedbacks,
      isFeedbackButtonsVisible: false,
      customAISlideStumpedText: [],
      isSubmittingFeedback: false,
      hasStoppedGenerating: false,
      gaSideLeftModalText: 'AI panel',
    }
  },
  computed: {
    ...mapGetters(['isEURegion']),
    ...mapGetters('docToSlideGenerator', ['docToSlideUniqueRequestId', 'docToSlideResponse', 'isDocToSlideGenerating']),
    ...mapState({
      presentation: (state) => state.presentation,
      isNewPresentation: (state) => state.isNewPresentation,
      slideActive: (state) => state.slideActive,
      sharePresentation: (state) => state.sharePresentation,
      currentUser: (state) => state.currentUser,
      slideGenerator: (state) => state.slideGenerator,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    preventTracking() {
      return this.preventTrackingDefault || this.mode === INPUT_MODE.FROM_URL
    },
    isGeneratedResultsVisible() {
      return this.isGeneratorContentVisible && this.isSearchAIModalContentVisible
    },
    hasInputPrompt() {
      return this.prompt && this.prompt.length > 0
    },
    isPickAnswerType() {
      return this.slideActive.id && this.slideActive.type === 'pickAnswer'
    },
    isPickAnswerSlide() {
      return this.isPickAnswerType && !this.slideActive.slideType
    },
    isTypeAnswerSlide() {
      return this.isPickAnswerType && this.slideActive.slideType === 'typeAnswer'
    },
    aiRequestSlideType() {
      if (!this.isModalContent) return null
      if (this.isPickAnswerSlide) return 'pickAnswer'
      if (this.isTypeAnswerSlide) return 'typeAnswer'
      if (this.slideActive.type === 'freestyle') return 'freestyle'
      return null
    },
    keyShowTooltip() {
      return `has-shown-ai-submit-button-tooltip-${this.currentUser.id}`
    },
    isHoverTooltipVisible() {
      return this.hasReachedTooltipAutoShownLimit || this.isSearchAIModalContentVisible ||
      !this.hasInputPrompt || this.isSubmitting
    },
    isAIGeneratorCollapsed() {
      return this.currentUser && this.currentUser.rememberSettings &&
        this.currentUser.rememberSettings.isAIGeneratorCollapsed
    },
    isExampleAreaHidden() {
      return (this.isAIGeneratorCollapsed && !this.isModalContent) || this.isSearchAIModalContentVisible
    },
    isAITextAreaHidden() {
      return this.isAIGeneratorCollapsed && !this.isModalContent
    },
    isModalContentNoSearchResult() {
      return this.isModalContent && !this.isSearchAIModalContentVisible
    },
    noChatResponse() {
      return !this.chatResponse || (this.chatResponse && !this.chatResponse.length)
    },
    isGenerateButtonDisabled() {
      return !this.hasInputPrompt || this.isSubmitting ||
        (!this.isDoneGenerating && !this.isReturnedSlideError)
    },
    docToQuizAttachmentTooltip() {
      if (!this.isDocToSlideGenerating) return this.$i18nPresenter.t('Upload file')
      return this.$i18nPresenter.t('A quiz is currently generating. Please wait to use this feature.')
    },
    quizPlaceholder() {
      if (this.placeholderCustom) return this.placeholderCustom
      return this.$i18nPresenter.t('Enter a topic or paste information…')
    },
    generateButtonIconClass() {
      return this.isGenerateButtonDisabled ? 'text-base-40 cursor-not-allowed' : 'text-secondary-100'
    },
    isMobileLayout() {
      return window.innerWidth <= 768
    },
  },
  watch: {
    hasInputPrompt() {
      this._setButtonTooltipContent()
    },
    isDoneGenerating() {
      this.isFeedbackButtonsVisible = this.isDoneGenerating && this.chatResponse.length > 0
    },
  },
  async mounted() {
    this._setTooltipLocalStorageLimit()
    EventBus.$on('slide-search-popup:open', this.backToChooseSlideType)
    EventBus.$on('slide-generator:unlock-add-slide', () => {
      if (!this.chatResponse || !this.chatResponse.length) return
      this.chatResponse.forEach((res) => {
        res.isAddingSlide = false
      })
    })
    EventBus.$on('slides-generator:generated', (socketData) => {
      this.handleGenerateSlidesSuccess(socketData)
    })
    EventBus.$on('slide-generator: ai_response_error', () => {
      this.handleGenerateSlidesError()
    })
    if (this.isModalContent) {
      this.isExpandExampleList = true
    }
    this._initPromptFromQueryParams()
  },
  destroyed() {
    EventBus.$off('slide-generator:unlock-add-slide')
    EventBus.$off('slides-generator:generated')
    EventBus.$off('slide-generator: ai_response_error')
  },
  methods: {
    ...mapActions([
      'setSlideActiveAction',
      'setCurrentUserAction',
      'setAIRequestLimitModalStateAction',
      'patchPresentationAction',
      'updateCurrentUserAction',
    ]),
    ...mapActions('slidesGeneratorFeedback', ['sendFeedbackOnSlidesGeneratorResult']),
    ...mapActions('docToSlideGenerator', ['toggleDocToSlideImportModalAction']),

    _initPromptFromQueryParams() {
      const prompt = lodashGet(this.$route, 'query.prompt', '')
      if (!prompt) return

      this.prompt = prompt
      this.$router.replace({ query: {} })
      this.handleGenerateSlides('From URL')
    },

    backToChooseSlideType() {
      if (this.isModalContent) return
      this.isSearchAIModalContentVisible = false
      this._changeAIModalSize('3xl')
      this.prompt = ''
      this.isExpandExampleList = false
      this._stopAllLoading()
      this.hasStoppedGenerating = true
      this.$emit('handleDiveInQuiz', false)
    },

    focusAIInput() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Focus input',
        prompt,
        null,
        {
          presentationId: this.presentation.id,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
    },

    _forceSetValueToReflectChanges(payload) {
      Object.keys(payload).forEach((key) => {
        this.$set(this, key, payload[key])
      })
    },

    _catchErrorGenerateSlides(error) {
      this._forceSetValueToReflectChanges({
        isDoneGenerating: true,
        isSubmitting: false,
        chatResponseError: true,
      })
      this._trackGAWhenGenerateSlidesFailed()
      Sentry.captureException(error, {
        extra: {
          inputText: this.prompt,
        },
      })
    },

    async _generateSlides(example) {
      try {
        const payload = this._setPayloadGenerateSlide(example)
        const res = await OpenAIApi.generateSlide(payload)
        if (!res) this._catchErrorGenerateSlides('_generateSlides error calling API')
      } catch (error) {
        this._catchErrorGenerateSlides(error)
      }
    },

    generateOrStopGeneratingSlides(mode, example) {
      if (!this.isDoneGenerating) {
        this.stopGeneratingSlides()
      } else {
        if (this.isGenerateButtonDisabled) return
        this.handleGenerateSlides(mode, example)
      }
    },

    _resetLayoutToDefault(mode) {
      this.hasStoppedGenerating = false
      this.mode = mode
      this.isDoneGenerating = false
      this.isReturnedSlideError = false
      this.chatResponseError = false
      this.isExampleResponse = false
      this.chatResponse = []
      if (this.isSubmitting) {
        this.resetProgressBar += 1
      }
    },

    async handleGenerateSlides(mode, example) {
      try {
        if (!this.isDoneGenerating || this.isSubmitting) return
        this._resetLayoutToDefault(mode)

        if (example) this.prompt = example
        this.$emit('submitPrompt', mode, this.prompt)
        if (!this.hasInputPrompt) return

        const { slideGeneratorRequestCount } = this.$store.state.currentUser

        if (slideGeneratorRequestCount >= REQUEST_MAXIMUM) {
          this.setAIRequestLimitModalStateAction(true)
          return
        }

        this.isSearchAIModalContentVisible = true
        this._changeAIModalSize('xl')
        this.isSubmitting = true
        this.buttonTooltipContent = this.tooltipText
        if (!this.isModalContent) {
          this.isExpandExampleList = false
        }
        this.$emit('handleDiveInQuiz', true)
        if (!this.isEURegion) {
          this._handleTrackGA(mode)
        }

        await this._generateSlides(example)
      } catch (error) {
        this._catchErrorGenerateSlides(error)
      }
    },

    _handleTrackGA(mode) {
      if (this.preventTracking) return
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > Generate',
        this.prompt,
        `Mode: ${mode}`,
        {
          presentationId: this.presentation.id,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
      mixpanelUtils.trackTimeEvent(this.gaGenerateFailedAction)
      mixpanelUtils.trackTimeEvent(this.gaGenerateSuccessfulAction)
    },

    trackGAWhenGenerateSlidesSucceeded() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaGenerateSuccessfulAction,
        this.prompt,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
      utils.sendCustomEventToGTM(
        'AI Quiz Generator > Generate > Successful',
        {
          presentationId: this.presentation.id,
        },
      )
    },

    _trackGAWhenGenerateSlidesFailed() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        this.gaGenerateFailedAction,
        this.prompt,
        null,
        {
          presentationId: this.presentation.id,
          touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal ?
            this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
        },
      )
    },

    _setPayloadGenerateSlide(example) {
      let payload = {
        message: this.prompt,
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        isExample: !!example,
      }
      if (this.aiRequestSlideType) {
        payload = {
          ...payload,
          aiRequestTypes: [this.aiRequestSlideType],
        }
      }
      return payload
    },

    async handleDoneGeneratingSlides(res) {
      // Delay 1s to confirm the last slide is sent via Socket before marking as done
      await utils.sleep(1000)
      this.isDoneGenerating = true
      const { slideGeneratorRequestCount } = res
      if (slideGeneratorRequestCount) {
        this._updateUserRequestCount(res.slideGeneratorRequestCount)
      }
      if (slideGeneratorRequestCount === REQUEST_THRESHOLD
      || slideGeneratorRequestCount === REQUEST_MAXIMUM) {
        this.setAIRequestLimitModalStateAction(true)
      }
    },

    handleGenerateSlidesSuccess(res) {
      const isCurrentRequest = res && res.userPrompt === this.prompt
      if (this.hasStoppedGenerating || !this.hasInputPrompt || !isCurrentRequest) return
      const hasResponse = res && res.slide && res.slide.data && res.slide.data.length && this.prompt === res.userPrompt
      const hasDone = res && res.done

      this.isSubmitting = false
      this.isDoneGenerating = false
      this.isSubmittingFeedback = false

      if (hasResponse) {
        if (!this.chatResponse) this.chatResponse = []
        this.chatResponseError = false
        this.isReturnedSlideError = false
        this.chatResponse = [...this.chatResponse, res.slide]
      }

      if (!hasResponse && hasDone) {
        this.handleDoneGeneratingSlides(res)
      }

      if (!this.chatResponseError) {
        this.trackGAWhenGenerateSlidesSucceeded()
      }
    },

    _stopAllLoading() {
      this.isDoneGenerating = true
      this.isSubmitting = false
      this.isSubmittingFeedback = false
    },

    _stopAllLoadingAndError() {
      this._stopAllLoading()
      this.chatResponseError = false
      this.isReturnedSlideError = false
    },

    handleGenerateSlidesError() {
      this._stopAllLoading()
      if (this.noChatResponse) {
        this.chatResponse = null
        this.chatResponseError = true
        this._trackGAWhenGenerateSlidesFailed()
      } else {
        this.isReturnedSlideError = true
      }
    },

    _updateUserRequestCount(slideGeneratorRequestCount) {
      this.setCurrentUserAction({
        ...this.currentUser,
        slideGeneratorRequestCount,
      })
    },

    _setLoadingButtonAdd(response, value) {
      this.chatResponse = this.chatResponse.map((res) => {
        return {
          ...res,
          isDisabled: value,
          isAddingSlide: response.id === res.id ? value : false,
        }
      })
    },

    trackGAWhenCreateNewSlide(response) {
      const trackingSlideType = this._setTrackingSlideTypeText(response.type)
      const presentationTracker = new PresentationMixpanelTracker(this.presentation)
      const slideTracker = new SlideMixpanelTracker(this.presentation, this.$route.name)
      slideTracker.setCustomProps({
        ...presentationTracker.getPresentationContext(),
        slideId: this.slideActive.id,
        slideType: trackingSlideType,
        touchPoint: this.slideGenerator.gaTouchPointWhenOpenAIModal && this.isModalContent ?
          this.slideGenerator.gaTouchPointWhenOpenAIModal : this.gaSideLeftModalText,
      })
      slideTracker.trackCreateNewSlide('AI-generated', trackingSlideType)
    },

    async handleAddSlideToPresentation(response) {
      if (response.isAddingSlide) return
      this._setLoadingButtonAdd(response, true)
      if (this.isModalContent) {
        EventBus.$on('ai-generator-modal: add-new-slide')
      }
      try {
        const [payload, newSlideIndex, activeIndex] = this._setPayloadCreateGeneratedSlide(
          response.id,
        )
        const res = await AIResponseAPI.createSlide(response.id, payload)
        this._handleAfterAddSlide(res.body.newSlide, newSlideIndex, activeIndex)
        OpenAIUtils.setAISlideCountForTracking(res.body, this)
      } catch (error) {
        utils.showToastError(this.$i18nPresenter.t('Cannot add this slide to current presentation. Please try again'))
        Sentry.captureException(error)
      } finally {
        this._setLoadingButtonAdd(response, false)
        this.trackGAWhenCreateNewSlide(response)
      }
    },

    _setTrackingSlideTypeText(type) {
      let text = type
      if (type === this.AIResponseType.TYPE_ANSWER) {
        text = `${this.AIResponseType.PICK_ANSWER} ${this.AIResponseType.TYPE_ANSWER}`
      }
      return text
    },

    _setPayloadCreateGeneratedSlide(aiResponseId) {
      const payload = {
        aiResponseId,
        presentationId: this.presentation.id,
        textColour: this.slideActive.textColour || '#313131',
        baseColour: this.slideActive.baseColour || '#ffffff',
        backgroundImage: this.slideActive.backgroundImage,
        visibility: this.slideActive.visibility,
      }

      // set payload.order for new slide
      const [newSlideIndex, activeIndex] = this._setPayloadInsertAfter(payload)
      return [payload, newSlideIndex, activeIndex]
    },

    _setPayloadInsertAfter(payload) {
      const activeIndex = this.$store.state.presentation.slides.findIndex((s) => {
        if (s.id) {
          return s.id === this.$store.state.slideActive.id
        }
        return s.timestamp === this.$store.state.slideActive.timestamp
      })
      let newSlideIndex = activeIndex
      if (activeIndex > -1 && !this.isNewPresentation) {
        const isNextSlideLeaderboard = this.presentation.slides[activeIndex + 1] && this.presentation.slides[activeIndex + 1].type === 'leaderboard'
        const activeSlideId = this.presentation.slides[activeIndex] &&
          this.presentation.slides[activeIndex].id

        if (!activeSlideId) {
          payload.insertAfter = this.presentation.slides[activeIndex - 1].order
        }
        if (activeSlideId && !isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex].order
          newSlideIndex = activeIndex + 1
        }
        if (activeSlideId && isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex].order + 1
          newSlideIndex = activeIndex + 2
        }
      }
      return [newSlideIndex, activeIndex]
    },

    _setNewSlideForContentSlide(newSlide) {
      const newCanvasBlocks = {}
      newSlide.canvasBlocks.forEach((block) => {
        block.id = generateBlockId(newSlide.id)
        block.h = generateBlockHash()
        newCanvasBlocks[block.id] = {
          id: block.id,
          type: 'RichText',
          tag: block.tag,
          content: block.content,
          style: block.style,
          h: block.h,
        }
      })
      newSlide.canvasBlocks = newCanvasBlocks
      this.updateCanvasBlocks(newSlide.canvasBlocks, newSlide.id) // CanvasBlocksUpdateMixin
      this.backupBlocks(newSlide.canvasBlocks, newSlide.canvasBlocks)
    },

    _makeSlideActiveAndSetSelectedSlideIds(newSlide) {
      EventBus.$emit('SideLeft: setSelectedSlideIds', [newSlide.id])
    },

    _deleteDefaultPollSlide(activeIndex) {
      this.$store.state.isNewPresentation = false
      const defaultSlide = this.presentation && this.presentation.slides && this.presentation.slides[activeIndex]
      const defaultSlideId = defaultSlide && defaultSlide.id
      if (!defaultSlide || !defaultSlideId) return
      const isDefaultPollSlide = defaultSlide && defaultSlideId && defaultSlide.type === 'poll' && !defaultSlide.title
      if (!isDefaultPollSlide) return
      // Delete default slide: poll
      SlideApi.updateSlideById(defaultSlideId, {
        deleted: true,
      })
    },

    async _handleAfterAddSlide(newSlide, newSlideIndex, activeIndex) {
      const isFreestyleSlideWithCanvasBlocks = newSlide.type === this.AIResponseType.FREESTYLE &&
        newSlide.canvasBlocks
      const isTypeAnswerSlide = newSlide.type === this.AIResponseType.PICK_ANSWER &&
        newSlide.slideType === this.AIResponseType.TYPE_ANSWER

      if (isFreestyleSlideWithCanvasBlocks) {
        this._setNewSlideForContentSlide(newSlide)
      } else if (isTypeAnswerSlide) {
        newSlide.otherCorrectQuiz = JSON.parse(newSlide.otherCorrectQuiz)
      }
      this.$store.state.presentation.slides[newSlideIndex] = newSlide
      this.setSlideActiveAction(newSlide)
      EventBus.$emit(`update-right-column-for-slide-title-${this.slideActive.id}`, newSlide.title)
      if (PresentationUtils.needSuggestPresentationTitle()) {
        this._handleSuggestPresentationTitle(newSlide, isFreestyleSlideWithCanvasBlocks)
      }
      await utils.sleep(250)
      if (!this.isMobileLayout) {
        this._makeSlideActiveAndSetSelectedSlideIds(newSlide, newSlideIndex)
      }
      utils.showToastSuccess(this.$i18nPresenter.t('Slide added successfully'))
      if (this.isNewPresentation) {
        this._deleteDefaultPollSlide(activeIndex)
      }
      if (newSlide.type === this.AIResponseType.PICK_ANSWER) {
        EventBus.$emit('addSlideLeaderboard')
      }
    },

    backupBlocks(oldData, newData, previousSelectingBlocks) {
      this.addActionToHistory({
        type: 'UpdateBlocks',
        data: {
          canvasBlocks: oldData,
          selectingBlocks: previousSelectingBlocks,
          slideActiveId: this.slideActive.id,
        },
        newData: {
          canvasBlocks: newData,
          selectingBlocks: previousSelectingBlocks,
          slideActiveId: this.slideActive.id,
        },
        slideActiveId: this.slideActive.id,
      })
    },

    _transformData(data) {
      return data.map((item) => {
        // Extracting the necessary fields
        const { id, index } = item
        const { type } = item.content
        const data = item.content.data.map((dataItem) => {
          if (type === 'pickAnswer' || type === 'typeAnswer') {
            return {
              questionTitle: dataItem.questionTitle,
              slideOptions: dataItem.slideOptions,
              correctQuizTypeAnswer: dataItem.correctQuizTypeAnswer,
              otherCorrectQuiz: dataItem.otherCorrectQuiz,
            }
          }

          return {
            type: dataItem.type,
            tag: dataItem.tag,
            content: dataItem.content,
            style: dataItem.style,
          }
        })

        return {
          id, index, type, data,
        }
      })
    },

    _changeAIModalSize(size) {
      if (!this.isModalContent) return
      this.$emit('changeAIModalSize', size)
    },

    expandExampleList() {
      if (this.isModalContent) return
      this.isExpandExampleList = true
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'AI Quiz Generator > More Example',
        null,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
    },

    handleCloseModal() {
      this.$emit('closeModal')
    },

    _setTooltipLocalStorageLimit() {
      this.buttonTooltipContent = null
      const countHasAutoShownTooltip = localStorageExpiry.getWithExpiry(this.keyShowTooltip) || 0
      this.hasReachedTooltipAutoShownLimit = countHasAutoShownTooltip && countHasAutoShownTooltip >= 3
      if (!this.hasReachedTooltipAutoShownLimit) {
        localStorageExpiry.setWithExpiry(this.keyShowTooltip, countHasAutoShownTooltip + 1, DAYS_30)
      }
    },

    _setButtonTooltipContent() {
      if (!this.hasInputPrompt) {
        this.buttonTooltipContent = null
      }
      setTimeout(() => {
        if (this.isHoverTooltipVisible) {
          this.buttonTooltipContent = this.tooltipText
          return
        }
        if (this.hasInputPrompt) {
          this.buttonTooltipContent = {
            content: this.tooltipText,
            show: this.hasInputPrompt,
          }
        }
      }, 300)
    },
    _getSlideTitle(newSlide, isFreestyleSlideWithCanvasBlocks) {
      if (isFreestyleSlideWithCanvasBlocks) {
        return textUtils.getFirstCanvasBlockContent(newSlide.canvasBlocks)
      }
      return newSlide.title
    },

    _handleSuggestPresentationTitle(newSlide, isFreestyleSlideWithCanvasBlocks) {
      const newSlideTitle = this._getSlideTitle(newSlide, isFreestyleSlideWithCanvasBlocks)
      PresentationUtils.handleSuggestPresentationTitle(newSlideTitle)
    },

    openModalDocToQuiz() {
      if (this.handleOpenModalDocToQuiz) {
        this.handleOpenModalDocToQuiz()
        return
      }
      if (this.isDisableEditPresentation || this.isDocToSlideGenerating) return
      this.toggleDocToSlideImportModalAction(true)
      this._trackOpenModalDocToQuiz()
    },
    _trackOpenModalDocToQuiz() {
      const eventAction = 'AI > Attachment icon > Open import modal'
      const trackData = {
        eventAction,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    autoExpandTextArea(textarea) {
      const heightLimit = 174

      textarea.style.height = '' /* Reset the height */
      textarea.style.height = `${Math.min(textarea.scrollHeight + 2, heightLimit)}px`
    },

    async _scrollToBottom() {
      this.resetProgressBar += 1
      await utils.sleep(500)
      const element = document.getElementById('ai-slides-generator-result-layout')
      if (!element) return
      element.scrollTo({
        top: element.scrollHeight + 200,
        behavior: 'smooth',
      })
    },

    _trackClickOnFeedbackButton(feedback) {
      const eventAction = AI_SLIDES_GENERATOR_REFINE_RESULTS
      const trackData = {
        eventAction,
        eventLabel: feedback,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    _resetLoadingFeedbackToDefault() {
      this.hasStoppedGenerating = false
      this.isSubmittingFeedback = true
    },

    async handleFeedbackQuiz(feedback) {
      try {
        if (this.isSubmittingFeedback) return
        this._resetLoadingFeedbackToDefault()
        this._scrollToBottom()
        this._trackClickOnFeedbackButton(feedback)
        await this.sendFeedbackOnSlidesGeneratorResult({
          feedbackMessage: feedback,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          allQuizzes: this.chatResponse,
          userPrompt: this.prompt,
        })
      } catch (error) {
        Sentry.captureException(error)
        this._forceSetValueToReflectChanges({
          isDoneGenerating: true,
          isSubmittingFeedback: false,
          isReturnedSlideError: !!this.chatResponseError,
          customAISlideStumpedText: ['Cannot generate quiz at the moment.'],
        })
      }
    },

    _trackGAWhenStopGeneratingSlides() {
      const eventAction = 'AI Slide Generator > Stop generating'
      const trackData = {
        eventAction,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventLabel: this.prompt,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },

    stopGeneratingSlides() {
      try {
        this._stopAllLoadingAndError()
        this.hasStoppedGenerating = true
        this._trackGAWhenStopGeneratingSlides()
      } catch (error) {
        Sentry.captureException(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.slide-generator {
  background-color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  &.expand {
    height: 100%;
    background-color: transparent;
  }
  &.is-modal-content {
    .slide-generator-input {
      &::v-deep {
        .aha-textarea {
          @apply h-16 min-h-16 overflow-auto my-0 mx-auto;
          max-height: 25rem;
          font-size: 20px;
          padding: 20px 16px;
          padding-left: 2.5rem;
          padding-right: 5.5rem;
          width: 624px;
        }
        .aha-textarea-limit {
          font-size: 20px;
          bottom: 1.25rem;
          right: 8rem;
        }
        .aha-textarea-button {
          bottom: 1.1rem;
          right: 6rem;
        }
        .aha-textarea__button-at-start {
          left: 5.75rem;
          top: 1.1rem;
        }
      }
    }
    .example-area {
      text-align: center;
      .example-box {
        margin-bottom: 8px;
        padding: 8px 12px;
      }
      .example-box__text {
        display: inline-block;
        font-size: 14px;
      }
    }
    .search-result {
      overflow: auto;
    }
    &.--has-search-content {
      .quiz-header-inner {
        padding: 10px 20px 20px 20px;
        &::v-deep .quiz-header-inner__title {
          padding-top: 10px;
          margin-bottom: 16px;
        }
      }
      .slide-generator-input {
        &::v-deep {
          .aha-textarea {
            width: 100%;
            height: 4.75rem;
            min-height: 2.75rem;
            padding: 0.6rem 4.3rem 0.6rem 0.75rem;
            font-size: 14px;
          }
          .aha-textarea-limit {
            right: 2.75rem;
            bottom: 0.75rem;
            font-size: 14px;
          }
          .aha-textarea-button {
            right: 1rem;
            bottom: 0.7rem;
          }
        }
      }
        .search-result {
          height: calc(90vh - 150px);
          .search-result-inner {
            padding-top: 1rem;
            .content-list {
              padding-bottom: 0;
              width: 468px;
              margin: 0 auto;
            }
          }
      }
    }
  }
  &.--has-expanded-examples {
    &::v-deep+.new-slide-content__layer {
      height: calc(100% - 272px);
    }
  }
  &.--has-search-content {
    .quiz-header-inner {
      padding: 10px 20px;
      &::v-deep .back-search {
        font-size: 0.9rem;
        .quiz-header-inner__title-text {
          font-size: 16px;
        }
      }
    }
  }
}

.quiz-header {
  padding: 16px 20px 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.quiz-header.expand {
  padding: 0;

  @media (min-width: 799px) {
    .quiz-header-inner {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}

.quiz-header-inner {
  position: relative;
  background-color: #7564D2;
  border-radius: 8px;
  padding: 0.9rem;
  transition: cubic-bezier(0.075, 0.82, 0.165, 1);
  overflow: hidden;
  border-top-right-radius: 0.25rem;
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
  &::after {
    z-index: 0;
    position: absolute;
    content: '';
    border-radius: 50%;
    background: linear-gradient(160.79deg, #1D1D54 9.94%, rgba(29, 29, 84, 0) 27.1%);
    opacity: 0.62;
    width: 1176.68px;
    height: 1279px;
    left: -40px;
    top: 52px;
  }
  .btn-generate-area {
    display: block;
    text-align: center;
  }
}
.search-result {
  height: 100%;
  margin-bottom: 52px;
  display: flex;
  background-color: #F9FBFF;
  overflow: hidden;
}

.slide-generator-input {
  z-index: 1;
  position: relative;
  width: 100%;
  &::v-deep {
    .aha-textarea {
      @apply pr-8 h-20 min-h-20 py-2;
      padding-left: 2.4rem;
      max-height: 10.75rem;
    }
    .aha-textarea__button-at-start {
      @apply absolute left-2 cursor-pointer;
      top: 0.4rem;
      .doc-to-quiz__upload-icon {
        @apply text-secondary-100 absolute;
        left: 2px;
        z-index: 1;
      }
      &:hover, &:active {
        .aha-textarea__button-bg {
          @apply bg-primary-50 rounded-full absolute opacity-100;
          width: 32px;
          height: 32px;
          z-index: 0;
          left: -5px;
          top: -4px;
        }
      }
    }
    .aha-textarea-limit {
      @apply hidden;
    }
    .aha-textarea-button {
      @apply absolute z-1;
      right: 0.6rem;
      top: 0.4rem;
      &:hover, &:active {
        .aha-textarea__button-bg {
          @apply bg-primary-50 rounded-full absolute opacity-100;
          width: 32px;
          height: 32px;
          right: -7px;
          top: -4.5px;
          z-index: -1;
        }
      }
      &:has(.generator_stop-icon) {
        @apply right-2;
      }
    }
    .aha-textarea__button-bg {
      @apply opacity-0 transition-opacity duration-200;
    }
  }
}
</style>
